<template>
  <div
    class="inset-box inset-box--non-member"
    data-ui="inset-box"
  >
    <h1
      v-if="heading"
      class="inset-box__heading"
      v-html="heading"
    />
    <p
      v-if="description"
      class="inset-box__copy"
      v-html="description"
    />
    <div :class="ctaClasses">
      <AddMembershipToCart
        :cta-text="addToCartText"
        :is-added-to-cart="membershipAddedToCart"
        :cta-full-width="true"
        class="inset-box__membership-btn"
        data-analytics-config="exclude"
        @click="handleMembershipClick"
        @error="(message) => $emit('error', message)"
        @success="$emit('success')"
        @warn="(message) => $emit('warn', message)"
      />
      <CdrButton
        v-if="enableBuyNowButton"
        :full-width="true"
        class="inset-box__buy-now-btn"
        modifier="secondary"
        data-ui="buy-now"
        :size="buttonSize"
        @click="buyMembershipNow"
      >
        Buy now
      </CdrButton>
      <div class="inset-box__footnote">
        <cdr-text
          v-if="heroType !== 'MEMBER'"
          tag="span"
          data-ui="logged-out-cta"
        >
          Already a member?
        </cdr-text>
        <CdrLink
          v-if="footnoteCta"
          :href="footnoteCta.target"
          :full-width="true"
          class="inset-box__membership-rewards-link"
          @click="handleCtaClick"
        >
          {{ footnoteCta.text }}
        </CdrLink>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue';
import {
  CdrLink,
  CdrText,
  CdrButton,
} from '@rei/cedar';
import AddMembershipToCart from '@rei/add-membership-to-cart';
import { metricsLink, metricsLinkAddToCart } from '../../../utils/analytics';
import addToCart from '../../../utils/buyMembershipNow';

const homePageInjectables = inject('homePageInjectables');

const props = defineProps({
  addToCartText: { type: String, required: true },
  description: { type: String, required: true },
  enableBuyNowButton: { type: Boolean, default: false },
  eyebrow: { type: String, required: true },
  footnoteCta: { type: Object, required: true },
  heading: { type: String, required: true },
  heroType: { type: String, required: true },
  membershipAddedToCart: { type: Boolean, required: true },
});

const emit = defineEmits(['error', 'success', 'warn']);

/* c8 ignore start */
const handleCtaClick = () => metricsLink('hero_Sign in to earn rewards');
/* c8 ignore end */

const ctaClasses = computed(() => ({
  'inset-box__ctas': true,
  'inset-box__ctas--buy-now': props.enableBuyNowButton,
}));

const handleCartResponse = ({ success, errorCode } = {}) => {
  if (success) {
    emit('success');
    document.dispatchEvent(new CustomEvent('rei.gnav.cartCountChange'));
    if (typeof window !== 'undefined' && window.location) window.location.assign('/checkout#contact-billing');
  } else if (errorCode) {
    if (errorCode === 3508) {
      if (typeof window !== 'undefined' && window.location) window.location.assign('/checkout#contact-billing');
    }
    console.error(errorCode);
  }
};

const handleMembershipClick = () => {
  metricsLinkAddToCart(`hero_${homePageInjectables?.addMembershipToCart}`);
};

const buyMembershipNow = () => {
  metricsLinkAddToCart('hero_Buy now');
  addToCart().then(handleCartResponse);
};
</script>

<style lang="scss">
.inset-box {
  padding: $cdr-space-three-x $cdr-space-one-x;
  text-align: center;
  position: relative;
  background-color: $cdr-color-background-primary;
  border-radius: $cdr-radius-softer;
  height: max-content;
}

.inset-box__heading {
  @include cdr-text-heading-serif-600;
  margin: 0 auto $cdr-space-half-x;
  text-wrap: balance;

  @include cdr-sm-mq-up {
    @include cdr-text-heading-serif-600;
  }
}

.inset-box__copy {
  @include cdr-text-body-300;
  margin: 0 auto $cdr-space-one-x;
  max-width: 600px;
  @include cdr-lg-mq-up {
    margin-bottom: $cdr-space-half-x;
    max-width: 600px;
  }
}

.inset-box__ctas {
  display: flex;
  flex-direction: column;
  align-items: center;
  .inset-box__membership-btn,
  .inset-box__buy-now-btn {
    width: 100%;
    max-width: 340px;
    margin-left: $cdr-space-one-x;
    margin-right: $cdr-space-one-x;
  }
}

// use unique layout if MLP Hero has no content in the slot
.hero--slot-inactive {
  @include cdr-sm-mq-up {
    .inset-box__ctas {
      max-width: 440px;
      margin: 0 auto $cdr-space-one-x;
    }
    .inset-box__ctas--buy-now {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: $cdr-space-one-x;
    }
    .inset-box__membership-btn,
    .inset-box__buy-now-btn {
      margin: 0;
      grid-row: 1;
    }

    .inset-box__membership-btn {
      grid-column: 1;
    }
    .inset-box__buy-now-btn {
      grid-column: 2;
    }
    .inset-box__footnote {
      grid-row: 2;
      grid-column: span 2;
    }
  }
}

.hero--slot-active {
  // A hack to make the error message not break the design
  .add-membership__error-box {
    @include cdr-sm-mq-up {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: calc(100% + 1.6rem);
      width: 270px;
    }

    @include cdr-md-mq-up {
      width: 320px;
    }
  }
  @include cdr-lg-mq-up {
    .inset-box {
      width: 375px;
      padding: $cdr-space-one-and-a-half-x;

      .inset-box__heading {
        margin-top: 0;
      }
    }

    .inset-box--non-member {
      .inset-box__heading {
        margin-bottom: $cdr-space-half-x;
        max-width: 320px;
      }
      .inset-box__copy {
        margin-bottom: $cdr-space-half-x;
        max-width: 392px;
      }
      .inset-box__footnote {
        margin-top: $cdr-space-half-x;
        display: flex;
        gap: $cdr-space-eighth-x;
        span {
          @include cdr-text-utility-sans-200;
        }
      }
      .inset-box__membership-rewards-link {
        @include cdr-lg-mq-up {
          @include cdr-text-utility-sans-200;
        }
      }
    }
  }
}

.inset-box__membership-btn {
  margin-bottom: $cdr-space-three-quarter-x;

  @include cdr-sm-mq-up {
    display: block;
  }
}

.inset-box__buy-now-btn {
  margin: 0 auto $cdr-space-half-x;
}

.inset-box--non-member {
  @include cdr-sm-mq-up {
    .inset-box__heading {
      margin-bottom: $cdr-space-three-quarter-x;
    }
    .inset-box__footnote {
      margin-top: $cdr-space-three-quarter-x;
    }
  }

  @include cdr-lg-mq-up {
    .inset-box__copy {
      margin-bottom: $cdr-space-three-quarter-x;
    }
  }
}
</style>
